import * as webcomponents from '../webcomponents';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const $: any;

declare global {
  interface Window {
    _mountCartDrawer: (args?: IMountCartDrawer) => Promise<HTMLElement | undefined>;
    _removeLegacyCartDrawer: () => void;
    _overrideCartDrawerComponents: (a: IComponentOverrides[], b?: Element) => void;
  }
}

interface ICartItem {
  COLLECTION_ID: number;
  COLLECTION_SUBTYPE: string;
  product: { sku: { SKU_BASE_ID: string } };
  coll_info?: {
    COLLECTION_TYPE?: string;
  };
  'sku.SKU_BASE_ID': string;
}

interface IEventResultData {
  ac_results: {
    result: {
      PREVIOUS_ITEM_QUANTITY: number;
      CARTITEM: {
        ITEM_QUANTITY: number;
        'sku.SKU_BASE_ID': string;
        CART_ID: number;
      };
      COLLECTION_ID?: number;
    };
  }[];
  coll_info?: {
    COLLECTION_ID?: number;
    COLLECTION_TYPE?: string;
  };
  trans_data?: {
    order: {
      items: {
        COLLECTION_ID: number;
        'sku.SKU_BASE_ID': string;
      }[];
      kits: {
        COLLECTION_ID: number;
        'sku.SKU_BASE_ID': string;
      }[];
    };
  };
  getItem(): ICartItem;
  getAllItems(): ICartItem[];
}

interface IComponentOverrides {
  override: {
    value?: string;
    styles?: string;
    attribute?: string;
    className?: string;
    text?: boolean;
    mode?: 'modal' | 'overlay';
  };
  elementName: CartDrawerSlotName | CartDrawerComponentName;
}

interface IOpenCartEvent {
  quantity: number;
  offerCode?: string;
  skuId?: string;
  collectionId?: number;
}

interface IMountCartDrawer {
  cartDrawerEndpoint?: string;
}

interface ICartDrawerInjection {
  aud_seg_allow: boolean;
  cart_drawer: boolean;
  cart_drawer_automount: boolean;
  has_audseg: boolean;
}

/**
 * These are the available slots in the cart drawer.
 * The rest of the cart drawer components are part of the Shadow DOM
 * and currently there is not support for overriding them.
 */
type CartDrawerSlotName =
  | 'cart-header'
  | 'cart-header-close'
  | 'messages'
  | 'thumbnail'
  | 'name'
  | 'shade-color'
  | 'shade-name'
  | 'size'
  | 'remove'
  | 'cart-count'
  | 'cart-estimated-total-label'
  | 'cart-estimated-total-value'
  | 'cart-additional-message'
  | 'button-go-shopping'
  | 'button-view-cart'
  | 'button-checkout';

/**
 * These are the available web components in the cart drawer.
 * Each component has a shadow root with the available slots.
 */
type CartDrawerComponentName =
  | 'cart-drawer'
  | 'cart-header'
  | 'cart-item'
  | 'cart-items-list'
  | 'cart-footer';

const atbEvents = ['addToCart.success', 'dwAddToCart.success'];

const isCartPage = () => window.location?.pathname?.includes('/checkout/viewcart.tmpl');

const removeCartDrawer = () => {
  const isVulcan = window._platform === 'vulcan';
  const isGem = '__NEXT_DATA__' in window && typeof window.__NEXT_DATA__ === 'object';

  const query = isVulcan ? '[data-behaviour="cart-drawer"]' : '[data-behavior-cart="modal"]';
  const legacyCartDrawer = document.querySelectorAll(query);
  legacyCartDrawer.forEach((cartDrawer: HTMLElement) => {
    if (isGem) {
      cartDrawer.style.display = 'none';
    } else {
      cartDrawer.remove();
    }
  });
};

const removeOfferPopup = () => {
  const popups = document.querySelectorAll('.sop--v2__success-popup');
  popups.forEach((popup: HTMLElement) => {
    popup.remove();
  });
};

const publishRemoveLegacyCart = () => {
  const newCartEvent = new CustomEvent('cart.removeLegacyCart', {
    detail: {
      removeLegacyCart: true
    }
  });
  window.dispatchEvent(newCartEvent);
};

const removeLegacyCartDrawer = () => {
  const isGem = '__NEXT_DATA__' in window && typeof window.__NEXT_DATA__ === 'object';
  if (isGem) {
    reMountOnCartIconUpdate();
  }
  publishRemoveLegacyCart();

  const observer = new MutationObserver(() => {
    removeCartDrawer();
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

const removeLegacyOfferPopup = () => {
  const observer = new MutationObserver(() => {
    removeOfferPopup();
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

/**
 * This method overrides the components inside the cart drawer
 * @param cartDrawer - The cart drawer element
 * @param override - The override object - value and at least one optional override are required
 * @param elementName - The name of the component to override - to override an inner element inside
 * a web component (slotted element) use the slot name (specified by CartDrawerSlotName); to override
 * a web component use the web component name (specified by CartDrawerComponentName)
 * @returns true if the component was found and overriden, false otherwise
 * @example overrideCartDrawerComponent(cartDrawer, { override: { value: 'Best sellers', text: true }, elementName: 'button-checkout' })
 * @example overrideCartDrawerComponent(cartDrawer, { override: {
 * styles: `.cart-button--drawer-cta-4 {
 *   border: solid 1px red;
 *   background: green;
 *   color: black; }`
 * },
 * elementName: 'cart-footer' })
 */
const overrideCartDrawerComponent = (
  cartDrawer: Element,
  { override, elementName }: IComponentOverrides
) => {
  const {
    value = '',
    attribute = '',
    text = false,
    className = '',
    styles = '',
    mode = 'overlay'
  } = override;
  const queryMode = `[slot="${mode}"]`;
  const query = styles ? `${elementName}${queryMode}` : `${queryMode} [slot="${elementName}"]`;
  const component = cartDrawer.querySelector(query);

  if (component) {
    if (attribute && component.getAttribute(attribute) !== value) {
      component.setAttribute(attribute, value);
    }
    if (text && component.textContent !== value) {
      component.textContent = value;
    }
    if (className && !component.classList.contains(className)) {
      component.classList.add(className);
    }
    if (styles) {
      overrideCartDrawerComponentStyles(component, styles);
    }

    return true;
  }
  return false;
};

/**
 * This method overrides the styles of a component inside the cart drawer
 * @param component - The component to override - it must be a web component not a slotted element
 * @param styles - The styles to override
 * @example overrideCartDrawerComponentStyles(component, ':root { color: black; }')
 */
const overrideCartDrawerComponentStyles = (component: Element, styles: string) => {
  if (component.shadowRoot) {
    try {
      const previousStyles = component.shadowRoot.adoptedStyleSheets;
      const overridedStyles = new CSSStyleSheet();
      overridedStyles.replaceSync(styles);
      component.shadowRoot.adoptedStyleSheets = [...previousStyles, overridedStyles];
    } catch {
      // support for old browsers
      const previousStyles = component.shadowRoot.querySelector('style');

      if (previousStyles) {
        previousStyles.innerText += styles;
      }
    }
  }
};

/**
 * This method observes the cart drawer and overrides the components
 * once they are rendered. Since the Cart 3.0 drawer is a server side
 * rendered component, we need to override the components everytime the
 * cart drawer is rendered.
 * @param componentOverrides - The overrides array
 * @param element - The cart drawer element
 * @example overrideCartDrawerComponents([
 *    { override: { value: 'Best sellers', text: true }, elementName: 'button-checkout' }
 *    { override: { value: '/products/8431/meilleures-ventes', attribute: 'data-cart-drawer-url-param' }, elementName: 'button-checkout' }
 * ], cart-drawer)
 */
const overrideCartDrawerComponents = (
  componentOverrides: IComponentOverrides[],
  element?: Element
) => {
  if (isCartPage()) {
    return;
  }

  const cartDrawer = element || document.querySelector('cart-drawer');

  const observer = new MutationObserver(() => {
    if (cartDrawer) {
      for (const { override, elementName } of componentOverrides) {
        overrideCartDrawerComponent(cartDrawer, { override, elementName });
      }
    }
  });

  observer.observe(cartDrawer || document.body, { childList: true, subtree: true });
};

const reMountOnCartIconUpdate = () => {
  const query = '[data-behavior-cart="icon"]';
  const cartIcon = document.querySelector(query);
  if (cartIcon) {
    const observer = new MutationObserver(() => {
      mountOnIcon(false);
    });
    observer.observe(cartIcon.parentNode?.parentNode || document.body, { childList: true });
  }
};

const mountOnIcon = (isVulcan?: boolean) => {
  const isGem = '__NEXT_DATA__' in window && typeof window.__NEXT_DATA__ === 'object';
  const query = isVulcan ? '[data-controller*="cart"]' : '[data-behavior-cart="icon"]';
  const icon = document.querySelector(query);
  if (icon && !icon.hasAttribute('data-mounted')) {
    const controller = icon.getAttribute('data-controller');
    const target = icon.getAttribute('data-controller');
    const actions = icon.getAttribute('data-action');

    icon.setAttribute('data-mounted', 'true');

    icon.setAttribute('data-controller', `${controller ? controller : ''} cart-icon`.trimStart());

    icon.setAttribute('data-cart-target', `${target ? target : ''} cartButton`.trimStart());

    icon.setAttribute(
      'data-action',
      `
        ${actions ? actions : ''}
        click->cart-icon#handleCartIconClick
        mouseenter->cart-icon#handleMouseEnter
        setCartIconCount@window->cart-icon#setItemsCount
      `.trimStart()
    );

    icon.setAttribute('data-cart-icon-cart-icon-url-value', '/checkout/viewcart.tmpl');

    /*
      .site-bag__link-count = MAC US
      .utility-nav__cart-count = EL US - Drupal
      [class*="utility-nav__cart"] = EL US - Gemini
      [data-cart-target="itemsCount"] = Vulcan
    */

    const countIconNodesOrder = [
      '.site-bag__link-count',
      '.utility-nav__cart-count',
      '[class*="utility-nav__cart"]',
      '[data-cart-target="itemsCount"]'
    ];

    const countIconNodes = countIconNodesOrder.reduce((accumulator, selector) => {
      const node = document.querySelector(selector);
      if (node) {
        return [...accumulator, node];
      } else {
        return [...accumulator];
      }
    }, []);

    const iconCount = countIconNodes.length > 0 ? countIconNodes[0] : undefined;

    if (iconCount) {
      iconCount.setAttribute('data-cart-icon-target', 'itemsCount');
    } else if (isGem) {
      const iconCountElement = document.createElement('div');
      iconCountElement.setAttribute('data-cart-icon-target', 'itemsCount');
      iconCountElement.setAttribute('data-testid', 'cartOverlayIconCounter');
      iconCountElement.setAttribute('class', 'utility-nav_utility-nav__cart__WxHwB');

      icon.parentNode?.insertBefore(iconCountElement, icon.nextSibling);
    }
  }

  return icon;
};

const openCartEvent = (params: IOpenCartEvent) => {
  const { quantity, collectionId, offerCode, skuId } = params;

  const detail = {
    cartMode: 'modal',
    quantity
  };

  if (skuId) {
    detail['skuId'] = skuId;
  }

  if (offerCode) {
    detail['offerCode'] = offerCode;
  }

  if (collectionId) {
    detail['collectionId'] = collectionId;
  }

  const newCartEvent = new CustomEvent('openCart', { detail });
  window.dispatchEvent(newCartEvent);
};

const fetchCartDrawer = () => {
  const newCartEvent = new CustomEvent('fetchCartDrawer');
  window.dispatchEvent(newCartEvent);
};

const atbOffer = (offerElement: Element) => {
  try {
    const offerCode = offerElement.getAttribute('data-offercode') || '';
    const eventName = offerCode ? `offer:${offerCode}:applied` : '';

    try {
      removeLegacyOfferPopup();
    } catch {
      // eslint-disable-next-line no-console
      console.info('[Cart 3.0 - Vulcan] Unable to remove legacy offer popup');
    }

    if (eventName) {
      $(document).on(eventName, () => {
        openCartEvent({ offerCode, quantity: 1 });
      });
    }
  } catch {
    fetchCartDrawer();
  }
};

const handleDWAddToCart = (collectionId: number, allItems: ICartItem[] = []) => {
  if (allItems.length > 0) {
    const itemInCart = allItems.find((item) => item.COLLECTION_ID === collectionId);

    if (
      itemInCart &&
      itemInCart.COLLECTION_SUBTYPE === 'EL_DW_DRAMMING' &&
      itemInCart['sku.SKU_BASE_ID']
    ) {
      return itemInCart['sku.SKU_BASE_ID'];
    }
  }
};

const getRecentAddedItem = (resultData: IEventResultData) => {
  const acResultsData = resultData.ac_results;
  const transData = resultData?.trans_data;
  if (acResultsData.length === 1) {
    return acResultsData[0].result;
  } else {
    const recentItemCollectionId = acResultsData?.[0].result.COLLECTION_ID;
    const recentItem = transData?.order?.items?.filter(
      (eachItem) => eachItem.COLLECTION_ID === recentItemCollectionId
    );
    const recentItemSkuId = recentItem?.[0]['sku.SKU_BASE_ID'];
    const filteredAcResult = acResultsData.filter((eachItem) => {
      return (
        eachItem.result?.CARTITEM?.CART_ID !== null &&
        eachItem.result?.CARTITEM?.['sku.SKU_BASE_ID'] === recentItemSkuId
      );
    });
    return filteredAcResult?.[0].result;
  }
};

const mapATBResults = (resultData: IEventResultData) => {
  const rawResults = resultData.ac_results;
  let collectionId: number | undefined;
  let quantity = 1;
  let skuId = '';

  if (rawResults) {
    const { PREVIOUS_ITEM_QUANTITY = 0, CARTITEM } = getRecentAddedItem(resultData);
    const { ITEM_QUANTITY = 1, 'sku.SKU_BASE_ID': skuBaseId = '' } = CARTITEM;
    // the data response sent in the event triggered by Drupal does not contain the added quantity
    quantity = ITEM_QUANTITY - PREVIOUS_ITEM_QUANTITY;
    skuId = skuBaseId;

    if (
      resultData.coll_info &&
      resultData.coll_info.COLLECTION_TYPE &&
      resultData.trans_data?.order
    ) {
      const COLLECTION_ID = resultData.coll_info.COLLECTION_ID;
      const skuData = resultData.trans_data.order.kits.find(
        (kit) => kit.COLLECTION_ID === COLLECTION_ID
      );
      if (skuData) {
        collectionId = COLLECTION_ID;
        skuId = skuData['sku.SKU_BASE_ID'];
      }
    }
  } else {
    const item = resultData.getItem();
    const allItems = resultData.getAllItems() || [];
    const { COLLECTION_ID, product } = item;
    const { sku } = product;
    const { SKU_BASE_ID: skuBaseId = '' } = sku;

    const hasEngraving = item?.coll_info?.COLLECTION_TYPE === 'MONO';

    const qtySelectorElement = document.querySelector(
      `[data-sku-base-id="${skuBaseId}"] [data-test-id="cart_product_quantity"]`
    );

    skuId = handleDWAddToCart(COLLECTION_ID, allItems) || skuBaseId;

    collectionId = COLLECTION_ID;
    quantity = hasEngraving ? 1 : parseInt(qtySelectorElement?.textContent || '1');
  }

  return { collectionId, quantity, skuId };
};

/**
 * This method requires jQuery since Drupal events are triggered using jQuery
 * so far, this is the only way to listen to the addToCart event we have found
 * but probably there are other events that could trigger the cart drawer
 */
const mountATBListener = (isVulcan?: boolean) => {
  const isDrp = 'jQuery' in window && typeof window.jQuery === 'function';
  const isGem = '__NEXT_DATA__' in window && typeof window.__NEXT_DATA__ === 'object';

  if (!isVulcan) {
    const offerElement = document.querySelector('[data-offercode]');
    if (isDrp) {
      atbEvents.forEach((atbEvent) => {
        $(window).on(atbEvent, function (_event: Event, resultData: IEventResultData) {
          try {
            const { collectionId, quantity, skuId } = mapATBResults(resultData);
            openCartEvent({ skuId, quantity, collectionId });
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('[Cart 3.0 - Vulcan] Unable to trigger Drupal ATB cart modal - ', error);
          }
        });
      });
      if (offerElement) {
        atbOffer(offerElement);
      }
    }

    if (isGem) {
      window.addEventListener('addToCart.success', (event: CustomEvent) => {
        const { cartItems } = event.detail;

        if (cartItems && cartItems.length) {
          const { skuBaseId = '', quantity: itemQuantity = 1 } = cartItems[0];
          const recentAddedItem = document.querySelector(
            `[data-cart-item-sku-id-value="${skuBaseId}"]`
          );
          const recentAddedItemPrevQty =
            recentAddedItem?.getAttribute('data-cart-item-qty-value') || '0';
          const previousItemQuantity = parseInt(recentAddedItemPrevQty, 10);
          const quantity =
            itemQuantity > previousItemQuantity
              ? itemQuantity - previousItemQuantity
              : itemQuantity;

          openCartEvent({ skuId: skuBaseId, quantity });
        }
      });
    }
  }
};

const mountCartDrawer = async (params?: IMountCartDrawer) => {
  if (isCartPage()) {
    return;
  }

  try {
    const { cartDrawerEndpoint } = params || {};
    const isVulcan = window._platform === 'vulcan';

    return webcomponents.registerController(['cart-icon', 'cart-drawer', 'cart-item']).then(() => {
      if (!mountOnIcon(isVulcan)) {
        // eslint-disable-next-line no-console
        console.info('[Cart 3.0 - Vulcan] Cart icon not found');

        return;
      }

      const cartDrawer = document.createElement('cart-drawer');
      cartDrawer.setAttribute('data-controller', 'cart-drawer');
      cartDrawer.setAttribute('data-cart-drawer-mode-value', 'overlay');
      cartDrawer.setAttribute(
        'data-action',
        'openCart@window->cart-drawer#open fetchCartDrawer@window->cart-drawer#updateContent'
      );
      if (cartDrawerEndpoint) {
        cartDrawer.setAttribute('data-cart-drawer-data-endpoint-value', cartDrawerEndpoint);
      }

      document.body.appendChild(cartDrawer);

      mountATBListener(isVulcan);
      // eslint-disable-next-line no-console
      console.info('[Cart 3.0 - Vulcan] Cart drawer mounted');

      return cartDrawer;
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[Cart 3.0 - Vulcan] Unable to mount cart drawer - ', error);
  }
};

const fetchCartConfigs = async () => {
  const url = '/cart/v3_0/config';

  return fetch(url)
    .then((response) => response.json())
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.warn('Could not fetch cart configs', { error, url });
      return {
        aud_seg_allow: false,
        cart_drawer: false,
        cart_drawer_automount: false,
        has_audseg: false
      };
    });
};

const getCookie = (cookieWithValue: string) => {
  return document.cookie
    .split(';')
    .some((item) => item.trim().toUpperCase().startsWith(cookieWithValue));
};

const forceMount = () => getCookie('VULCANIZE_CART=1');

const cartDrawerInjection = async () => {
  const {
    aud_seg_allow = false,
    cart_drawer = false,
    cart_drawer_automount = false,
    has_audseg = false
  }: ICartDrawerInjection = await fetchCartConfigs();
  const isValidSeg = has_audseg && aud_seg_allow && (cart_drawer_automount || forceMount());

  if (!has_audseg || isValidSeg) {
    if (cart_drawer || forceMount()) {
      if (cart_drawer_automount || forceMount()) {
        removeLegacyCartDrawer();
        await mountCartDrawer();
      } else {
        window._mountCartDrawer = mountCartDrawer;
        window._overrideCartDrawerComponents = overrideCartDrawerComponents;
        window._removeLegacyCartDrawer = removeLegacyCartDrawer;
      }
    } else {
      // eslint-disable-next-line no-console
      console.info('[Cart 3.0 - Vulcan] Cart Drawer is not enabled');
    }
  }
};

cartDrawerInjection();
